import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Container,
  useTheme,
  Snackbar,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import StrategiesTable from "./StrategiesTable";
import StrategiesDialog from "./StrategiesDialog";
import { Helmet } from "react-helmet-async";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
  removeActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { deleteStrategies, updateStrategy } from "../../utils/restAPI";
import { selectStrategies } from "../../Redux/Reducers/selectors";
import { setStrategies } from "../../Redux/Reducers/strategiesSlice";
import { toast } from "react-toastify";

const Strategies = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStrategy, setEditStrategy] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const strategies = useSelector(selectStrategies);
  const handleAdd = () => {
    navigate("/strategies/add");
  };

  const handleEdit = (strategy) => {
    setEditStrategy(strategy);
    setDialogOpen(true);
  };

  const handleDelete = async (strategy) => {
    const idToDelete = strategy?.strategyId || strategy?.id;
    if (!idToDelete) return;

    if (window.confirm(`Are you sure you want to delete strategy "${strategy?.name}"?`)) {
      try {
        dispatch(addActiveRequests("delete_strategy"));
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Deleting Strategy..."));

        const resp = await deleteStrategies([idToDelete]);
        if (resp?.error) throw new Error(resp.message || "Delete failed");

        dispatch(
          setStrategies((strategies || []).filter((s) => (s?.strategyId || s?.id) !== idToDelete))
        );

        dispatch(removeActiveRequests("delete_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        toast.success("Strategy deleted");
      } catch (error) {
        console.error("Failed to delete strategy", error);
        dispatch(removeActiveRequests("delete_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        const displayMessage =
          error?.message === "You do not have permission to delete the requested strategies"
            ? "You do not have permissions to delete this strategy"
            : error?.message;
        toast.error(displayMessage);
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditStrategy(null);
  };

  const handleSave = async (strategy) => {
    
    if (!strategy || Object.keys(strategy).length === 0) return;

    if (editStrategy) {
      try {
        dispatch(addActiveRequests("modify_strategy"));
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Updating Strategy..."));

        const fields = {};
        if (strategy.name !== undefined) fields.name = strategy.name;
        if (strategy.description !== undefined) fields.description = strategy.description;
        if (strategy.owner !== undefined) fields.owner = strategy.owner;
        if (strategy.tags !== undefined) fields.tags = strategy.tags;
        if (strategy.categories !== undefined) fields.categories = strategy.categories;

        const resp = await updateStrategy(editStrategy.strategyId, fields);
        if (resp?.error) throw new Error(resp.message || "Update failed");

        // Update Redux store with the new values so the edit modal shows them correctly on next open
        const updatedStrategies = (strategies || []).map((s) => {
          if (s.strategyId !== editStrategy.strategyId) return s;
          return {
            ...s,
            description: fields.description ?? s.description,
            owner: fields.owner ?? s.owner,
            metadata: {
              ...s.metadata,
              tags: fields.tags ?? s.metadata?.tags,
              categories: fields.categories ?? s.metadata?.categories,
            },
          };
        });
        dispatch(setStrategies(updatedStrategies));

        dispatch(removeActiveRequests("modify_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        toast.success("Strategy updated");
      } catch (error) {
        console.error("Failed to update strategy", error);
        dispatch(removeActiveRequests("modify_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        toast.error(error?.message || "Failed to update strategy");
      }
    } else {
      sendWebSocketMessage(
        `{"action":"new_strategy", "data":${JSON.stringify(strategy)}}`
      );
      dispatch(addActiveRequests("new_strategy"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg(`Adding Strategy...`));
      // setSnackbar({ open: true, message: "Strategy created successfully" });
    }
    setDialogOpen(false);
    setEditStrategy(null);
  };

  return (
    <>
      <Helmet>
        <title>Strategies | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Strategies
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage your trading strategies and investment approaches
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <StrategiesTable
              strategies={strategies}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
            />
          </Card>
        </Container>

        <StrategiesDialog
          key={editStrategy?.strategyId || "new"}
          open={dialogOpen}
          onClose={handleDialogClose}
          strategy={editStrategy}
          onSave={handleSave}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    </>
  );
};

export default Strategies;
